import { useState } from 'react';
import { Globe, Loader2, X, AlertCircle, CheckCircle2, FlaskConical } from 'lucide-react';

interface DomainPurchaseConfirmationProps {
  domain: string;
  price: number | null; // Price in cents, null while loading
  isLoadingPrice: boolean;
  priceError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  isPurchasing: boolean;
  purchaseError: string | null;
  isTestMode?: boolean;
}

export function DomainPurchaseConfirmation({
  domain,
  price,
  isLoadingPrice,
  priceError,
  onConfirm,
  onCancel,
  isPurchasing,
  purchaseError,
  isTestMode = false,
}: DomainPurchaseConfirmationProps) {
  const [termsAccepted, setTermsAccepted] = useState(false);

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div className={`border-2 rounded-xl p-5 space-y-4 ${isTestMode ? 'bg-amber-50 border-amber-300' : 'bg-white border-primary-200'}`}>
      {/* Test Mode Banner */}
      {isTestMode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 border border-amber-300 rounded-lg">
          <FlaskConical className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">TEST MODE — No actual charge will be made</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isTestMode ? 'bg-amber-200' : 'bg-primary-100'}`}>
            <Globe className={`w-5 h-5 ${isTestMode ? 'text-amber-700' : 'text-primary-600'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{domain}</h3>
            <p className="text-sm text-gray-500">Domain registration {isTestMode && '(simulated)'}</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          disabled={isPurchasing}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Cancel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Price display */}
      <div className="bg-gray-50 rounded-lg p-4">
        {isLoadingPrice ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Getting price...</span>
          </div>
        ) : priceError ? (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{priceError}</span>
          </div>
        ) : price !== null ? (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Annual registration</span>
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(price)}
              <span className="text-sm font-normal text-gray-500">/year</span>
            </span>
          </div>
        ) : null}
      </div>

      {/* Terms checkbox */}
      {price !== null && !priceError && (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={isPurchasing}
            className="w-5 h-5 mt-0.5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">
            I agree to the{' '}
            <a
              href="https://niftee.ai/terms.html#domain-registration"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline font-medium"
            >
              Domain Registration Terms
            </a>
            . Domain registrations are non-refundable. Prices are subject to change at renewal.
          </span>
        </label>
      )}

      {/* Purchase error */}
      {purchaseError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{purchaseError}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={isPurchasing}
          className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!termsAccepted || isPurchasing || isLoadingPrice || !!priceError || price === null}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isTestMode
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {isPurchasing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isTestMode ? 'Simulating...' : 'Processing...'}
            </>
          ) : (
            <>
              {isTestMode ? <FlaskConical className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {isTestMode ? 'Test Purchase' : 'Buy Now'} {price !== null && `— ${formatPrice(price)}`}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
